export const STATUS_LABEL = {
  trial: "Пробный",
  active: "Активен",
  suspended: "Отключён",
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

export function formatDateRu(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTimeRu(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Статус акцепта оферты/ПДн владельцем клуба (клиент платформы). */
export function ownerLegalLabel(owner) {
  if (!owner) return null;
  if (owner.legal_accepted) {
    const at =
      owner.legal_terms_accepted_at || owner.legal_consent_pd_accepted_at;
    return at ? `Соглашения · ${formatDateRu(at)}` : "Соглашения приняты";
  }
  return "Соглашения не приняты";
}

export function ownerLegalAccepted(owner) {
  return Boolean(owner?.legal_accepted);
}

export function formatRelativeActivity(iso) {
  if (!iso) return "Нет активности";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  if (diff < 0) return formatDateRu(iso);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Только что";
  if (mins < 60) return `${mins} мин. назад`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч. назад`;
  const days = Math.floor(hours / 24);
  if (days < 14) return `${days} дн. назад`;
  return formatDateRu(iso);
}

export function daysLeftLabel(days) {
  if (days == null) return "—";
  if (days < 0) return `${Math.abs(days)} дн. назад`;
  return `${days} дн.`;
}

export function clinicEffectiveStatus(clinic) {
  const billing = clinic?.billing;
  if (clinic?.status === "suspended" || billing?.status === "suspended") {
    return "suspended";
  }
  if (billing?.is_demo_expired) return "expired";
  if (billing?.is_paid && !billing?.is_trial) return "active";
  if (billing?.is_trial) return "trial";
  if (clinic?.status === "active" && billing?.is_paid) return "active";
  if (clinic?.status === "trial") return "trial";
  return clinic?.status || "trial";
}

export function clinicStatusLabel(clinic) {
  const billing = clinic?.billing;
  if (billing?.status_label) return billing.status_label;
  const effective = clinicEffectiveStatus(clinic);
  if (effective === "expired") return "Демо истекло";
  return STATUS_LABEL[effective] || STATUS_LABEL[clinic?.status] || clinic?.status || "—";
}

export function statusClass(status, billing) {
  // status arg kept for callers; prefer billing when present
  if (status === "suspended" || billing?.status === "suspended") {
    return "statusSuspended";
  }
  if (billing?.is_demo_expired || (billing?.days_left != null && billing.days_left < 0 && !billing?.is_paid)) {
    return "statusExpired";
  }
  if (billing?.is_paid && !billing?.is_trial) return "statusActive";
  if (billing?.is_trial) return "statusTrial";
  if (billing?.is_expiring_soon) return "statusTrial";
  if (status === "trial") return "statusTrial";
  if (status === "active") return "statusActive";
  return "statusNeutral";
}

export function activityTone(iso) {
  if (!iso) return "muted";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff > 14 * DAY_MS) return "danger";
  if (diff > 7 * DAY_MS) return "warn";
  return "ok";
}

export function filterClinics(clinics, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return clinics;
  return clinics.filter((c) => {
    const owner = c.owner || {};
    const hay = [
      c.name,
      c.subdomain,
      c.plan?.name,
      c.billing?.plan_label,
      owner.full_name,
      owner.email,
      owner.phone,
      c.phone,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}

export function summarizeClinics(clinics) {
  const list = clinics || [];
  const expiring14 = list.filter(
    (c) =>
      c.billing?.is_paid &&
      !c.billing?.is_trial &&
      c.billing?.days_left != null &&
      c.billing.days_left >= 0 &&
      c.billing.days_left <= 14,
  ).length;
  const inactive7d = list.filter((c) => {
    const at = c.activity?.last_activity_at;
    if (!at) return true;
    return Date.now() - new Date(at).getTime() > 7 * DAY_MS;
  }).length;
  const outreach = list.filter((c) => isOutreachTarget(c));
  return {
    total: list.length,
    active: list.filter((c) => clinicEffectiveStatus(c) === "active").length,
    trial: list.filter((c) => clinicEffectiveStatus(c) === "trial").length,
    suspended: list.filter((c) => clinicEffectiveStatus(c) === "suspended").length,
    expired: list.filter((c) => clinicEffectiveStatus(c) === "expired").length,
    expiring14,
    inactive7d,
    outreach: outreach.length,
    outreachWithPhone: outreach.filter((c) => getClinicPhone(c)).length,
    clients: list.reduce((sum, c) => sum + (c.stats?.clients || 0), 0),
    trainers: list.reduce((sum, c) => sum + (c.stats?.trainers || 0), 0),
  };
}

/** Причины для прозвона менеджером. */
export const OUTREACH_REASON_LABELS = {
  demo_unpaid: "Демо не оплачено",
  demo_expired: "Демо истекло",
  inactive: "Нет активности",
  expiring: "Подписка истекает",
  overdue: "Оплата просрочена",
  never_logged_in: "Не заходили в систему",
};

export function getClinicPhone(clinic) {
  return (
    String(clinic?.owner?.phone || clinic?.phone || "").trim() || ""
  );
}

export function getClinicEmail(clinic) {
  return String(clinic?.owner?.email || "").trim() || "";
}

export function getOutreachReasons(clinic) {
  const reasons = [];
  const billing = clinic?.billing || {};
  const at = clinic?.activity?.last_activity_at;
  const loginAt = clinic?.activity?.last_staff_login_at;
  const inactiveMs = at ? Date.now() - new Date(at).getTime() : Infinity;

  if (billing.is_demo_expired) {
    reasons.push("demo_expired");
  } else if (billing.is_trial && !billing.is_paid) {
    reasons.push("demo_unpaid");
  }

  if (
    billing.days_left != null &&
    billing.days_left < 0 &&
    !billing.is_demo_expired
  ) {
    reasons.push("overdue");
  } else if (
    billing.days_left != null &&
    billing.days_left >= 0 &&
    billing.days_left <= 14 &&
    (billing.is_paid || billing.is_trial)
  ) {
    reasons.push("expiring");
  }

  if (!loginAt && !at) {
    reasons.push("never_logged_in");
  } else if (inactiveMs > 7 * DAY_MS) {
    reasons.push("inactive");
  }

  return reasons;
}

export function isOutreachTarget(clinic) {
  if (clinic?.status === "suspended") return false;
  return getOutreachReasons(clinic).length > 0;
}

export function filterOutreachSegment(clinics, segmentId) {
  const list = (clinics || []).filter((c) => c.status !== "suspended");
  if (segmentId === "outreach") {
    return list.filter((c) => isOutreachTarget(c));
  }
  if (segmentId === "demo_unpaid") {
    return list.filter((c) =>
      getOutreachReasons(c).includes("demo_unpaid"),
    );
  }
  if (segmentId === "demo_expired") {
    return list.filter((c) =>
      getOutreachReasons(c).includes("demo_expired"),
    );
  }
  if (segmentId === "inactive") {
    return list.filter((c) => {
      const r = getOutreachReasons(c);
      return r.includes("inactive") || r.includes("never_logged_in");
    });
  }
  if (segmentId === "expiring") {
    return list.filter((c) => getOutreachReasons(c).includes("expiring"));
  }
  if (segmentId === "overdue") {
    return list.filter((c) => getOutreachReasons(c).includes("overdue"));
  }
  if (segmentId === "with_phone") {
    return list.filter((c) => isOutreachTarget(c) && getClinicPhone(c));
  }
  return list;
}

function csvEscape(value) {
  const str = value == null ? "" : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function buildOutreachExportRows(clinics) {
  return (clinics || []).map((c) => {
    const reasons = getOutreachReasons(c);
    const reasonLabels = reasons
      .map((id) => OUTREACH_REASON_LABELS[id] || id)
      .join("; ");
    return {
      club_name: c.name || "",
      subdomain: c.subdomain || "",
      owner_name: c.owner?.full_name || "",
      phone: getClinicPhone(c),
      email: getClinicEmail(c),
      status: clinicStatusLabel(c),
      call_reasons: reasonLabels,
      plan: c.billing?.plan_label || c.plan?.name || c.plan_tier || "",
      paid_until: c.billing?.paid_until
        ? String(c.billing.paid_until).slice(0, 10)
        : "",
      days_left:
        c.billing?.days_left == null ? "" : String(c.billing.days_left),
      last_activity: c.activity?.last_activity_at
        ? String(c.activity.last_activity_at).slice(0, 16).replace("T", " ")
        : "",
      clients: String(c.stats?.clients ?? 0),
      trainers: String(c.stats?.trainers ?? 0),
      sessions_30d: String(c.stats?.sessions_30d ?? 0),
      registered_at: c.createdAt
        ? String(c.createdAt).slice(0, 10)
        : "",
      club_id: c.id || "",
    };
  });
}

const OUTREACH_CSV_COLUMNS = [
  { key: "club_name", header: "Клуб" },
  { key: "subdomain", header: "Поддомен" },
  { key: "owner_name", header: "Владелец" },
  { key: "phone", header: "Телефон" },
  { key: "email", header: "Email" },
  { key: "status", header: "Статус" },
  { key: "call_reasons", header: "Причина прозвона" },
  { key: "plan", header: "Тариф" },
  { key: "paid_until", header: "Оплачено до" },
  { key: "days_left", header: "Дней осталось" },
  { key: "last_activity", header: "Последняя активность" },
  { key: "clients", header: "Клиенты" },
  { key: "trainers", header: "Тренеры" },
  { key: "sessions_30d", header: "Занятия 30д" },
  { key: "registered_at", header: "Дата регистрации" },
  { key: "club_id", header: "ID клуба" },
];

export function downloadOutreachCsv(clinics, filenamePrefix = "baysport-prozvony") {
  const rows = buildOutreachExportRows(clinics);
  const header = OUTREACH_CSV_COLUMNS.map((c) => c.header).join(",");
  const body = rows
    .map((row) =>
      OUTREACH_CSV_COLUMNS.map((col) => csvEscape(row[col.key])).join(","),
    )
    .join("\n");
  const csv = `\uFEFF${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const stamp = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `${filenamePrefix}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return rows.length;
}
